import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["select", "menu", "input", "display", "checkmark", "tags", "options"]
  static values = {
    multiple: { type: Boolean, default: false },
    remoteUrl: { type: String, default: "" }
  }

  connect() {
    this.syncUI()
    this.externalClickListener = this.hide.bind(this)
    document.addEventListener("mousedown", this.externalClickListener)
    this.filterTimer = null
    this.requestCounter = 0
  }

  disconnect() {
    document.removeEventListener("mousedown", this.externalClickListener)
    if (this.filterTimer) clearTimeout(this.filterTimer)
  }

    toggle(e) {

      e.preventDefault()

      e.stopPropagation()

      this.menuTarget.classList.contains("hidden") ? this.show() : this.close()

    }

  

    // 改名避讳：pick -> choose

    choose(e) {

      e.preventDefault()

      e.stopPropagation()

      const val = e.currentTarget.dataset.selectValue

      const opt = Array.from(this.selectTarget.options).find(o => o.value == val)

      

      if (opt) {

        if (this.multipleValue) {

          opt.selected = !opt.selected

        } else {

          Array.from(this.selectTarget.options).forEach(o => o.selected = false)

          opt.selected = true

          this.close()

        }

        this.syncUI()

        this.selectTarget.dispatchEvent(new Event('change'))

      }

    }

  show() {
    this.menuTarget.classList.remove("hidden")
    this.inputTarget.focus()
    this.inputTarget.value = ""
    if (this.hasRemoteUrl()) {
      this.renderLoading()
      this.fetchRemoteOptions("")
    } else {
      this.filterItems("")
    }
  }

  close() {
    this.menuTarget.classList.add("hidden")
  }

  remove(e) {
    e.preventDefault()
    e.stopPropagation()
    const val = e.currentTarget.dataset.value
    const opt = Array.from(this.selectTarget.options).find(o => o.value == val)
    if (opt) {
      opt.selected = false
      this.syncUI()
      this.selectTarget.dispatchEvent(new Event('change'))
    }
  }

  filter(e) {
    const query = e.target.value
    if (!this.hasRemoteUrl()) {
      this.filterItems(query)
      return
    }

    if (this.filterTimer) clearTimeout(this.filterTimer)
    this.filterTimer = setTimeout(() => {
      this.renderLoading()
      this.fetchRemoteOptions(query)
    }, 180)
  }

  filterItems(q) {
    const query = q.toLowerCase()
    this.menuTarget.querySelectorAll("[data-select-value]").forEach(item => {
      const text = item.textContent.toLowerCase()
      item.classList.toggle("hidden", !text.includes(query))
    })
  }

  stop(e) { e.stopPropagation() }

  hide(e) {
    if (!this.element.contains(e.target)) this.close()
  }

  syncUI() {
    if (!this.hasSelectTarget) return
    
    if (this.multipleValue) {
      this.renderTags()
    } else {
      this.renderSingle()
    }

    const selectedValues = Array.from(this.selectTarget.selectedOptions).map(o => o.value.toString())
    this.checkmarkTargets.forEach(el => {
      const isSel = selectedValues.includes(el.dataset.value.toString())
      const p = el.closest(".group")
      if (p) isSel ? p.classList.add("selected") : p.classList.remove("selected")
    })
  }

  hasRemoteUrl() {
    return this.remoteUrlValue && this.remoteUrlValue.length > 0
  }

  async fetchRemoteOptions(query) {
    if (!this.hasRemoteUrl()) return

    const currentRequest = ++this.requestCounter
    const url = new URL(this.remoteUrlValue, window.location.origin)
    url.searchParams.set("q", query || "")

    try {
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!response.ok) return

      const payload = await response.json()
      if (currentRequest !== this.requestCounter) return

      const options = Array.isArray(payload.options) ? payload.options : []
      this.renderRemoteOptions(options)
    } catch (error) {
      console.error("[select] remote options render failed:", error)
      this.renderError()
    }
  }

  renderRemoteOptions(options) {
    if (!this.hasOptionsTarget) return

    const selectedMap = new Map(
      Array.from(this.selectTarget.selectedOptions)
        .filter(o => o.value !== "")
        .map(o => [o.value.toString(), o.text])
    )

    this.selectTarget.querySelectorAll("option[data-remote='1']").forEach((opt) => opt.remove())

    options.forEach((item) => {
      const value = (item.value ?? "").toString()
      const label = (item.label ?? "").toString()
      if (!value.length) return
      if (Array.from(this.selectTarget.options).some((opt) => opt.value.toString() === value)) return

      const optionNode = document.createElement("option")
      optionNode.value = value
      optionNode.text = label
      optionNode.dataset.remote = "1"
      optionNode.selected = selectedMap.has(value)
      this.selectTarget.appendChild(optionNode)
    })

    if (options.length === 0) {
      const emptyChoice = this.multipleValue ? "" : this.buildEmptyChoiceHtml()
      this.optionsTarget.innerHTML = `${emptyChoice}<div class="px-3 py-2 text-sm text-gray-400">无匹配用户</div>`
      this.syncUI()
      return
    }

    const emptyChoice = this.multipleValue ? "" : this.buildEmptyChoiceHtml()
    const optionItems = options.map((item) => {
      const value = this.escapeHtml((item.value ?? "").toString())
      const label = this.escapeHtml((item.label ?? "").toString())
      return `
        <div class="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer flex items-center justify-between group"
             data-action="mousedown->select#choose"
             data-select-label="${label}"
             data-select-value="${value}">
          <span>${label}</span>
          <div class="text-primary opacity-0 group-[.selected]:opacity-100 transition-opacity" data-select-target="checkmark" data-value="${value}">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
      `
    }).join("")

    this.optionsTarget.innerHTML = `${emptyChoice}${optionItems}`

    this.syncUI()
  }

  buildEmptyChoiceHtml() {
    return `
      <div class="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer flex items-center justify-between group"
           data-action="mousedown->select#choose"
           data-select-label="请选择..."
           data-select-value="">
        <span>请选择...</span>
        <div class="text-primary opacity-0 group-[.selected]:opacity-100 transition-opacity" data-select-target="checkmark" data-value="">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      </div>
    `
  }

  renderLoading() {
    if (!this.hasOptionsTarget) return
    this.optionsTarget.innerHTML = '<div class="px-3 py-2 text-sm text-gray-400">搜索中...</div>'
  }

  renderError() {
    if (!this.hasOptionsTarget) return
    this.optionsTarget.innerHTML = '<div class="px-3 py-2 text-sm text-red-400">搜索失败，请重试</div>'
  }

  escapeHtml(text) {
    return String(text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }

  renderSingle() {
    const opt = this.selectTarget.selectedOptions[0]
    if (opt && opt.value !== "") {
      this.displayTarget.innerHTML = `<span class="text-gray-900 text-sm pl-1">${opt.text}</span>`
    } else {
      this.displayTarget.innerHTML = '<span class="text-gray-400 text-sm pl-1">请选择...</span>'
    }
  }

  renderTags() {
    const selected = Array.from(this.selectTarget.selectedOptions).filter(o => o.value !== "")
    if (selected.length === 0) {
      this.tagsTarget.innerHTML = '<span class="text-gray-400 text-sm pl-1">请选择...</span>'
    } else {
      this.tagsTarget.innerHTML = selected.map(o => `
        <span class="inline-flex items-center gap-x-1 px-2 py-0.5 rounded bg-gray-100 border border-gray-200 text-xs text-gray-700">
          ${o.text}
          <button type="button" data-action="mousedown->select#remove" data-value="${o.value}" class="text-gray-400 hover:text-red-500">
            <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </span>`).join('')
    }
  }
}
